import { DateTime } from "luxon";
import { RaRecord } from "react-admin";
import { LessonExtension } from "./LessonExtension";
import { OnlineMeeting } from "./OnlineMeeting";
import { PostalAddress } from "./PostalAddress";
import { t } from "./types";
import { type z } from "zod";
import { TheoryLecture, TheoryLectureAbbreviation } from "./TheoryLecture";
import { Money } from "./Money";
import { schemas } from "../generated/serverClient";
import { DrivingLicenseClass } from "./DrivingLicenseClass";

export interface BaseAutovioCalendarEvent extends RaRecord {
  id: string;
  start: DateTime;
  end: DateTime;
  drivingSchoolId: string;
  branchId?: string;
  bufferTimes?: BufferTimes;
  resources?: string[];
  deletionReason?: string;
  editReason?: string;
  editCount: number;
  editWithReasonCount: number;
  createdAt: DateTime;
  createdBy: string;
  updatedAt?: DateTime;
  updatedBy?: string;
  deleted: boolean;
  deletedAt?: DateTime;
  deletedBy?: string;
  vogelId?: string;
}

interface BufferTimes {
  readonly preparationTime: number; // in Minutes
  readonly wrapUpTime: number; // in Minutes
}

export interface TheoryLessonStudentAttendance {
  rsvp: "accepted" | "rejected";
  agreedCancellationPolicy: any;
  bookedAt?: DateTime;
  bookedBy?: string;
  canceledAt?: DateTime;
  canceledBy?: string;
  cancelReason?: string;
  attended?: boolean;
  noShow?: boolean;
  bookedTrainingIds?: string[];
}

export const RsvpEnum = t.enum(["pending", "accepted", "rejected", "declined", "canceled", "no-show"]);

export type Rsvp = z.infer<typeof RsvpEnum>;

export interface PaidLessonStudentAttendance {
  agreedCancellationPolicy: any;
  bookedAt?: DateTime;
  bookedTrainingId?: string | undefined;
  bookedTrainingIds?: string[];
  canceledAt?: DateTime;
  canceledAtShortNotice?: boolean;
  cancelReason?: string;
  declinedAt?: DateTime;
  rsvp: Rsvp;
  uid: string;
}

export interface TheoryLesson extends BaseAutovioCalendarEvent {
  type: "TheoryLesson";
  courseUid?: string;
  /**
   * Deprecated, replaced by `theoryLecture` property.
   * 1..18 (1..12 - Grundstoff, 13,14 - Zusatzstoff for B, 15..18 - Zusatzstoff for A, 96 for B96)
   */
  theoryUnit?: number;
  theoryLecture: TheoryLecture;
  theoryLectureAbbreviation: TheoryLectureAbbreviation;
  instructorId: string;
  students: { [uid: string]: TheoryLessonStudentAttendance };
  numBookedStudents: number;
  maxStudents: number;
  location: PostalAddress | OnlineMeeting;
  branchId: string;
  noteForInstructor: string;
  noteForStudents: string;
}

export interface TheoryExam extends BaseAutovioCalendarEvent {
  type: "TheoryExam";
  student: PaidLessonStudentAttendance;
  startLocation: PostalAddress;
  status?:
    | "invited"
    | "booked"
    | "declinedByStudent"
    | "canceledByStudent"
    | "canceledByInstructor"
    | "passed"
    | "failed"
    | "no-show";
  theoryExamResult?: "passed" | "failed";
  theoryExamResults?: { [bookedTrainingId: string]: "passed" | "failed" };
  noteForInstructor?: string;
  noteForStudent?: string;
}

export const BillingWhenSchema = t.literal("asap").nullish();
export const PaymentStatusSchema = t.enum(["pending", "success", "failed", "disputeLost", "refunded"]).nullish();

export interface DrivingLesson extends BaseAutovioCalendarEvent {
  type: "DrivingLesson";
  drivingLessonType:
    | "normal"
    | "unterweisungAmFahrzeug"
    | "praktischeUnterweisung"
    | "ueberlandfahrt"
    | "autobahnfahrt"
    | "nachtfahrt"
    | "simulator"
    | "praktischePruefung"
    | "schaltkompetenz"
    | "fahrprobe";
  drivingLicenseClass?: DrivingLicenseClass;
  extensions: LessonExtension[];
  /** undefined for drivingLessonType: "simulator". */
  instructorId?: string;
  student: PaidLessonStudentAttendance;
  startedAt?: DateTime;
  endedAt?: DateTime;
  startLocation: PostalAddress;
  isBooked: boolean;
  status?:
    | "invited"
    | "booked"
    | "declinedByStudent"
    | "canceledByStudent"
    | "canceledByInstructor"
    | "finished"
    | "passed" // <-- for practical exam
    | "failed" // <-- for practical exam
    | "no-show";
  vehicleId?: string | null;
  vehicle2Id?: string | null;
  studentUsesOwnMotorcycle?: boolean;
  remarks?: string; // <-- deprecated, use noteForStudent instead
  price: Money;
  isManualPrice?: boolean;
  noteForInstructor: string;
  noteForStudent: string;
  billingWhen?: z.output<typeof BillingWhenSchema>;
  paymentStatus?: z.output<typeof PaymentStatusSchema>;
}

export type PracticalExam = DrivingLesson & {
  drivingLessonType: "praktischePruefung";
  practicalExamResult?: "passed" | "failed";
};

export interface RemoteLesson extends BaseAutovioCalendarEvent {
  type: "RemoteLesson";
  extensions: LessonExtension[];
  instructorId: string;
  student: PaidLessonStudentAttendance;
  price: Money;
  noteForInstructor: string;
  noteForStudent: string;
}

export type PaidLesson = DrivingLesson | RemoteLesson;

export type ASFCourseSessionType = z.infer<typeof schemas.ASFCourseSessionTypeEnum>;

export interface ASFCourseSessionAttendance {
  attended?: boolean;
  bookedAt?: DateTime;
  noShow?: boolean;
  rsvp?: never;
}

export interface ASFCourseSession extends BaseAutovioCalendarEvent {
  type: "ASFCourseSession";
  sessionType: ASFCourseSessionType;
  courseUid?: string;
  instructorId: string;
  numBookedParticipants: number;
  participants: { [uid: string]: ASFCourseSessionAttendance };
  location: PostalAddress & { type?: "PostalAddress" };
  branchId: string;
  noteForInstructor: string;
  noteForParticipants: string;
}

export interface CourseLesson extends BaseAutovioCalendarEvent {
  type: "CourseLesson";
  courseId: string;
  courseModuleName: string;
  courseModuleColor: string;
  location: PostalAddress | OnlineMeeting;
  /** A composed id of the format "<drivingSchoolId>:<roomId>". */
  roomId?: null | string;
  /** The AUTOVIO user id, *not* the Firebase Auth user id. */
  lecturerId?: null | string;
  /** `undefined` or `null` if the lecturer is not an instructor. */
  instructorId?: null | string;
  noteForInstructor: string;
  noteForParticipants: string;
}

export const OtherEventDurationEnum = t.enum(["halfDay", "fullDay", "halfDayInTheMorning", "halfDayInTheAfternoon"]);
export type OtherEventDuration = z.infer<typeof OtherEventDurationEnum>;

export interface OtherEvent extends BaseAutovioCalendarEvent {
  type: "Other";
  instructorId: string;
  categoryId: string;
  notes?: string;
  otherEventDuration?: OtherEventDuration;
  vehicleId?: string | null;
}

export const VehicleEventTypeEnum = schemas.VehicleEventTypeEnum;
export type VehicleEventType = z.infer<typeof VehicleEventTypeEnum>;

export interface VehicleEvent extends BaseAutovioCalendarEvent {
  type: "VehicleEvent";
  vehicleId: string;
  vehicleEventType: VehicleEventType;
  noteForInstructor: string;
  instructorId?: never;
  student?: never;
}

interface UnknownEvent extends BaseAutovioCalendarEvent {
  type: "Unknown";
}

export type AutovioCalendarEvent =
  | TheoryLesson
  | TheoryExam
  | DrivingLesson
  | RemoteLesson
  | ASFCourseSession
  | CourseLesson
  | OtherEvent
  | VehicleEvent
  | UnknownEvent;

export const isTheoryLesson = (record: any): record is TheoryLesson => record?.type === "TheoryLesson";
export const isTheoryExam = (record: any): record is TheoryExam => record?.type === "TheoryExam";
export const isDrivingLesson = (record: any): record is DrivingLesson => record?.type === "DrivingLesson";
export const isPracticalExam = (record: any): record is PracticalExam =>
  record?.drivingLessonType === "praktischePruefung";
export const isRemoteLesson = (record: any): record is RemoteLesson => record?.type === "RemoteLesson";
export const isPaidLesson = (record: any): record is PaidLesson => isDrivingLesson(record) || isRemoteLesson(record);
export const isASFCourseSession = (record: any): record is ASFCourseSession => record?.type === "ASFCourseSession";
export const isCourseLesson = (record: any): record is CourseLesson => record?.type === "CourseLesson";
export const isOtherEvent = (record: any): record is OtherEvent => record?.type === "Other";
export const isVehicleEvent = (record: any): record is VehicleEvent => record?.type === "VehicleEvent";

export type CombinedExamResult = "passed" | "failed" | "canceledByStudent" | "studentNoShow";

export function getCombinedExamResult(exam: TheoryExam | PracticalExam): CombinedExamResult | "" {
  const perTrainingResults = isTheoryExam(exam) ? Object.values(exam.theoryExamResults ?? {}) : [];
  const examResult = isTheoryExam(exam)
    ? (perTrainingResults.find((result) => result === "failed") ?? perTrainingResults[0] ?? exam.theoryExamResult)
    : exam.practicalExamResult;
  if (!examResult) return "";
  if (examResult === "passed") return "passed";
  const rsvp = exam.student?.rsvp;
  if (rsvp === "rejected") return "canceledByStudent";
  if (rsvp === "no-show") return "studentNoShow";
  return "failed";
}

export function isPlanned(event: TheoryExam | PaidLesson | PracticalExam): boolean {
  return event.status === "invited" || event.status === "booked";
}

export function isPendingInvitation(event: AutovioCalendarEvent): boolean {
  return (isTheoryExam(event) || isPaidLesson(event)) && event.student.rsvp === "pending";
}

export function hasInstructor(event: AutovioCalendarEvent): boolean {
  return !isTheoryExam(event) && !!event.instructorId;
}

function wasImported(event: AutovioCalendarEvent): boolean {
  return event.createdBy === "00000000-0000-0000-0000-000000000000";
}

export function canBeDeleted(event: AutovioCalendarEvent): boolean {
  if (event.deleted) {
    return false;
  }
  if (isVehicleEvent(event)) {
    return true;
  }
  if (isOtherEvent(event)) {
    return true;
  }
  if (isTheoryExam(event)) {
    return event.student.rsvp === "pending";
  }
  if (isPaidLesson(event)) {
    if (event.student.rsvp === "pending") {
      return true;
    }
    if (wasImported(event) && event.price.amount === 0) {
      return true;
    }
  }
  return false;
}

export function canBeHistoricallyDeleted(event: AutovioCalendarEvent): boolean {
  return !event.deleted && !canBeDeleted(event) && isPaidLesson(event) && !!(event.startedAt || event.endedAt);
}

export function canBeCanceled(event: AutovioCalendarEvent): boolean {
  if (event.deleted) {
    return false;
  }
  if (isTheoryLesson(event)) {
    return true;
  }
  if (isTheoryExam(event)) {
    return event.student.rsvp === "accepted" && !event.startedAt && !event.endedAt;
  }
  if (isPaidLesson(event)) {
    return event.student.rsvp === "accepted" && !event.startedAt && !event.endedAt;
  }
  return false;
}

export type TheoryLessonAttendanceState = "attended" | "no-show" | "cancelled" | "booked";

export const getTheoryLessonAttendanceState = (
  studentId: string,
  theoryLesson: TheoryLesson,
): TheoryLessonAttendanceState => {
  if (theoryLesson.students[studentId].attended) {
    return "attended";
  } else if (theoryLesson.students[studentId].noShow) {
    return "no-show";
  } else if (theoryLesson.students[studentId].rsvp === "rejected") {
    return "cancelled";
  } else if (theoryLesson.students[studentId].rsvp === "accepted") {
    return "booked";
  } else {
    throw new Error(
      `Couldn't determine attendance status (theoryLessonId: ${theoryLesson.id}, studentId: ${studentId})`,
    );
  }
};

export const getASFCourseSessionAttendanceState = (
  studentId: string,
  asfCourseSession: ASFCourseSession,
): "attended" | "no-show" | "unknown" => {
  if (asfCourseSession.participants[studentId].attended) {
    return "attended";
  } else if (asfCourseSession.participants[studentId].noShow) {
    return "no-show";
  } else {
    return "unknown";
  }
};

export function hasBufferTimes(calendarEvent: AutovioCalendarEvent): boolean {
  const { bufferTimes } = calendarEvent;
  return !!bufferTimes && (bufferTimes.preparationTime > 0 || bufferTimes.wrapUpTime > 0);
}
